// Modal dialog for creating or editing a category. Replaces the previous
// inline form so opening it never reflows the tree.

import { useEffect, useState, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  flattenCategories,
  type Category,
  type CategoryFormPayload,
} from "./types";
import { useCreateCategory, useUpdateCategory } from "./use-categories";

interface Props {
  /** Full tree, used to populate the parent picker. */
  cats: Category[];
  /** When set, dialog is in edit mode for this category. */
  editing: Category | null;
  /** When set and `editing` is null, pre-selects this id as parent (for "Add child"). */
  defaultParentId?: string | null;
  onClose: () => void;
}

export function CategoryFormDialog({ cats, editing, defaultParentId, onClose }: Props) {
  const [name, setName] = useState(editing?.name ?? "");
  const [slug, setSlug] = useState(editing?.slug ?? "");
  const [parentId, setParentId] = useState<string>(
    editing?.parentId ?? defaultParentId ?? "",
  );
  const [clientError, setClientError] = useState<string | null>(null);

  const create = useCreateCategory();
  const update = useUpdateCategory(editing?.id);
  const mutation = editing ? update : create;

  // Esc to close — match the pattern users expect from other dialogs.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const flat = flattenCategories(cats, editing?.id);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setClientError(null);
    if (!name.trim()) {
      setClientError("Tên danh mục không được trống");
      return;
    }
    const payload: CategoryFormPayload = {
      name: name.trim(),
      slug: slug.trim() || slugify(name),
      parentId: parentId || null,
    };
    mutation.mutate(payload, { onSuccess: onClose });
  }

  const errorMsg = clientError ?? (mutation.error ? translateApiError(mutation.error) : null);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className="relative w-full max-w-md rounded-lg border bg-card p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cat-dialog-title"
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="cat-dialog-title" className="text-lg font-semibold">
            {editing ? "Sửa danh mục" : "Thêm danh mục"}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng">
            <X size={16} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cat-name">Tên danh mục *</Label>
            <Input
              id="cat-name"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cat-slug">Slug</Label>
            <Input
              id="cat-slug"
              placeholder="tự sinh từ tên nếu để trống"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cat-parent">Danh mục cha</Label>
            <select
              id="cat-parent"
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">— Gốc —</option>
              {flat.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {errorMsg && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose}>
              Huỷ
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Đang lưu…" : editing ? "Cập nhật" : "Thêm"}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

// Lightweight client-side slug generator. Strips diacritics, lowercases, and
// keeps only [a-z0-9-]. BE re-normalises so this is best-effort UX.
function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
